import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { formatTime, getTodayTime, saveTodayTime, getTotalTime, getTotalTodayTime, getTotalYesterdayTime, getTotalThisWeekTime, getTodayKey } from '../utils/timeUtils';
import { useAuth } from '../context/AuthContext';
import { dataService } from '../services/dataService';
import useIsMobile from '../hooks/useIsMobile';
import Accordion from './common/Accordion';
import { fetchAllVocabWords } from '../services/wordBankService';
import type { VocabWord } from '../types/vocab';
import { fetchDefinitionForWord } from '../services/dictionaryService';
import { useSubscription } from '../hooks/useSubscription';
import PaywallModal from './PaywallModal';
import { useSessionTracker } from '../hooks/useSessionTracker';
import { supabase } from '../services/supabaseClient';
import { fetchDrillWords, removeDrillWord } from '../services/drillWordsService';

const FREE_WORD_LIMIT = 200;

type WordData = VocabWord;

const mapCustomRowToWordData = (row: any): WordData => ({
  Word: row.word || '',
  Definition: row.definition || '',
  'Similar words category': row.similar_words_category ?? '',
  'Similar words ': row.similar_words ?? '',
  ID: row.id,
  source: row.source ?? 'user',
  isCustom: true
});

const mergeAndSortWords = (customEntries: WordData[], baseEntries: WordData[]) => {
  const deduped = new Map<string, WordData>();
  [...customEntries, ...baseEntries].forEach(entry => {
    const key = (entry.Word || '').toLowerCase();
    if (!deduped.has(key)) {
      deduped.set(key, entry);
    }
  });
  return Array.from(deduped.values()).sort((a, b) => a.Word.localeCompare(b.Word));
};

interface AnswerRecord {
  word: string;
  definition: string;
  isCorrect: boolean;
}

interface ReportRecord {
  id?: string;
  word: string;
  type: 'definition' | 'synonym';
  reportedDefinition?: string;
  reportedSynonym?: string;
  note?: string;
  timestamp: number;
}

interface FlashcardGameProps {
  words?: WordData[];
  isFastAttack?: boolean;
  onComplete?: () => void;
  sessionLabel?: string;
  progressKey?: string;
  initialCardIndex?: number;
  initialAnsweredWords?: string[];
  onCardProgress?: (cardIndex: number, answeredWords: string[]) => void;
}


const PRIMARY        = 'var(--color-primary)';
const PRIMARY_LIGHT  = 'var(--color-success-light)';
const BORDER         = 'var(--color-border)';
const SURFACE        = 'var(--color-surface)';
const SURFACE_MUTED  = 'var(--color-surface-muted)';
const TEXT_MUTED     = 'var(--color-text-muted)';

const SESSION_SIZE = 10;

const cardBaseStyle: React.CSSProperties = {
  backgroundColor: SURFACE,
  border: `1px solid ${BORDER}`,
  borderRadius: '20px',
  padding: '24px 16px',
};

const statPillStyle: React.CSSProperties = {
  flex: '1 1 120px',
  minWidth: '100px',
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  padding: '8px 0',
};

const answerButtonBase: React.CSSProperties = {
  width: '100%',
  borderRadius: '16px',
  border: `1px solid ${BORDER}`,
  backgroundColor: '#fff',
  padding: '13px 14px',
  minHeight: '56px',
  fontSize: '14px',
  fontWeight: 400,
  textAlign: 'left',
  transition: 'all 0.15s ease',
  cursor: 'pointer',
  color: 'var(--color-text)',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
};

const FlashcardGame: React.FC<FlashcardGameProps> = ({ words: providedWords, isFastAttack = false, onComplete, sessionLabel, progressKey, initialCardIndex, initialAnsweredWords, onCardProgress }) => {
  const { user } = useAuth();
  const { isPro } = useSubscription();
  const location = useLocation();
  const isDrillMode = new URLSearchParams(location.search).get('drill') === '1';
  const [showPaywall, setShowPaywall] = useState(false);
  const [words, setWords] = useState<WordData[]>(providedWords || []);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [selectedAnswerText, setSelectedAnswerText] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [frozenOptions, setFrozenOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(!providedWords);
  const [correctAnswers, setCorrectAnswers] = useState<AnswerRecord[]>([]);
  const [incorrectAnswers, setIncorrectAnswers] = useState<AnswerRecord[]>([]);
  const [showBank, setShowBank] = useState(false);
  const [isInitialMount, setIsInitialMount] = useState(true);
  const [reviewMode, setReviewMode] = useState(false); // Toggle for reviewing only incorrect words
  const scrollPositionRef = useRef<number>(0);
  const userScrolledRef = useRef<boolean>(false);
  const [jumpToNumber, setJumpToNumber] = useState<string>('');
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportNote, setReportNote] = useState('');
  const [reportedIssues, setReportedIssues] = useState<ReportRecord[]>([]);
  const [isShuffled, setIsShuffled] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const touchEndY = useRef<number | null>(null);
  const [swipeOffsetY, setSwipeOffsetY] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [nextCardIndex, setNextCardIndex] = useState<number | null>(null);
  const cardContainerRef = useRef<HTMLDivElement>(null);
  const [elapsedTime, setElapsedTime] = useState(0); // Time in seconds
  const [todayTime, setTodayTime] = useState(0); // Time spent today
  const [totalTime, setTotalTime] = useState(0); // Total time across all modes
  const [totalTodayTime, setTotalTodayTime] = useState(0); // Total time today across all modes
  const sessionStartTimeRef = useRef<number | null>(null);
  const todaySessionStartRef = useRef<number | null>(null);
  const initialSavedTimeRef = useRef<number>(0); // Track initial saved time to avoid double counting
  const initialTodayTimeRef = useRef<number>(0); // Track initial saved today time
  const [pendingWordIndex, setPendingWordIndex] = useState<number | null>(null);
  const [focusMode, setFocusMode] = useState(false);
  const [metricsPanelOpen, setMetricsPanelOpen] = useState(false);
  const isMobile = useIsMobile();
  const [selectedView, setSelectedView] = useState<'stats' | 'time'>('stats');
  const [newWord, setNewWord] = useState('');
  const [newDefinition, setNewDefinition] = useState('');
  const [newSynonyms, setNewSynonyms] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [savingCustomWord, setSavingCustomWord] = useState(false);
  const [customWordError, setCustomWordError] = useState<string | null>(null);
  const [customWordSuccess, setCustomWordSuccess] = useState<string | null>(null);
  const [showCustomWordForm, setShowCustomWordForm] = useState(false);
  const [customWordEntries, setCustomWordEntries] = useState<WordData[]>([]);
  const [showOnlyCustom, setShowOnlyCustom] = useState(false);
  const [drillWords, setDrillWords] = useState<string[]>([]);

  // Load drill words from Supabase when in drill mode
  useEffect(() => {
    if (!isDrillMode || !user) return;
    fetchDrillWords(user.id).then(setDrillWords);
  }, [isDrillMode, user]);
  const [shuffleOrder, setShuffleOrder] = useState<number[] | null>(null);
  const [showJumpSection, setShowJumpSection] = useState(false);
  const [showMoreOptions, setShowMoreOptions] = useState(false);

  // Session tracker hook
  const {
    isSessionActive,
    sessionNumber,
    lastSessionCardCount,
    lifetimeSeenCount,
    startSession,
    recordCardSeen,
    endSession,
  } = useSessionTracker('flashcard');

  // Distractor pool refs — shuffled once per word load, advanced sequentially
  const distractorPoolRef = useRef<string[]>([]);
  const distractorIndexRef = useRef(0);
  const recentCorrectRef = useRef<string[]>([]);
  // Full vocab list for distractors — populated even in FastAttack (30-word) mode
  // so wrong answers are drawn from the entire 1100+ word bank, not just the active group.
  const allWordsRef = useRef<WordData[]>([]);

  // Fast Attack: track which words have been answered this session
  const fastAttackAnsweredRef = useRef<Set<string>>(new Set());
  const [fastAttackAnsweredCount, setFastAttackAnsweredCount] = useState(0);

  useEffect(() => {
    const shouldFocus = !showResult && (selectedAnswer !== null || correctAnswers.length + incorrectAnswers.length > 0);
    setFocusMode(shouldFocus);
    if (!shouldFocus) {
      setMetricsPanelOpen(false);
    }
  }, [selectedAnswer, showResult, correctAnswers.length, incorrectAnswers.length]);

  const shouldShowMetrics = !focusMode || metricsPanelOpen;

const viewToggleWrapStyle: React.CSSProperties = {
  display: 'flex',
  gap: '8px',
  flexWrap: 'wrap',
  marginBottom: '12px'
};

const viewToggleButtonStyle = (active: boolean): React.CSSProperties => ({
  padding: '8px 14px',
  borderRadius: '999px',
  border: 'none',
  cursor: 'pointer',
  backgroundColor: active ? PRIMARY : 'var(--color-surface-muted)',
  color: active ? '#fff' : '#0f172a',
  fontSize: '13px',
  fontWeight: active ? 600 : 500,
  transition: 'background 0.2s ease, color 0.2s ease'
});

const SignInAlert: React.FC = () => (
  <div
    style={{
      borderRadius: '12px',
      border: `1px solid ${BORDER}`,
      backgroundColor: '#fff7ed',
      padding: '12px 16px',
      color: '#92400e',
      fontSize: '13px',
      marginTop: '12px'
    }}
  >
    Sign in to save your flashcard progress and sync across devices.
  </div>
);

  const saveAnswerToSupabase = async (word: WordData, isCorrect: boolean) => {
    if (!user) return;
    try {
      await dataService.flashcardAnswers.insert({
        user_id: user.id,
        word_id: word.ID || word.Word,
        word: word.Word,
        definition: word.Definition,
        correct: isCorrect,
        timestamp: Date.now(),
        date: getTodayKey()
      });
    } catch (e) {
      console.error('Error saving answer to Supabase:', e);
    }
  };

  const clearSupabaseProgress = async () => {
    if (!user) return;
    try {
      await Promise.all([
        dataService.flashcardAnswers.deleteAll(user.id),
        dataService.reportedIssues.deleteByGameMode(user.id, 'flashcard'),
        dataService.userProgressState.delete(user.id, 'flashcard', 'last_word_index')
      ]);
    } catch (e) {
      console.error('Error clearing Supabase flashcard data:', e);
    }
  };

  const handleClearData = async () => {
    if (!window.confirm('Are you sure you want to clear all saved progress? This cannot be undone.')) {
      return;
    }

    setCorrectAnswers([]);
    setIncorrectAnswers([]);
    setReportedIssues([]);
    try {
      localStorage.removeItem('gre_correct_answers');
      localStorage.removeItem('gre_incorrect_answers');
      localStorage.removeItem('gre_reported_issues');
    } catch (e) {
      console.error('Error clearing localStorage:', e);
    }

    if (user) {
      await clearSupabaseProgress();
    }
  };

  // Load data (local first, then Supabase if available)
  useEffect(() => {
    let isMounted = true;

    const loadFromLocalStorage = () => {
      try {
        const savedCorrect = localStorage.getItem('gre_correct_answers');
        const savedIncorrect = localStorage.getItem('gre_incorrect_answers');
        const savedReports = localStorage.getItem('gre_reported_issues');

        if (savedCorrect) {
          try {
            const parsed = JSON.parse(savedCorrect);
            if (isMounted) {
              setCorrectAnswers(parsed);
            }
          } catch (e) {
            console.error('Error parsing correct answers from localStorage:', e);
          }
        }

        if (savedIncorrect) {
          try {
            const parsed = JSON.parse(savedIncorrect);
            if (isMounted) {
              setIncorrectAnswers(parsed);
            }
          } catch (e) {
            console.error('Error parsing incorrect answers from localStorage:', e);
          }
        }

        if (savedReports) {
          try {
            const parsed = JSON.parse(savedReports);
            if (isMounted) {
              setReportedIssues(parsed);
            }
          } catch (e) {
            console.error('Error parsing reported issues from localStorage:', e);
          }
        }

        if (!isFastAttack) {
          try {
            const savedWordIndex = localStorage.getItem('gre_last_word_index');
            if (savedWordIndex) {
              const index = parseInt(savedWordIndex, 10);
              if (!isNaN(index) && index >= 0) {
                setPendingWordIndex(index);
              }
            }
          } catch (e) {
            console.error('Error loading saved word index:', e);
          }
        }

        const savedTime = localStorage.getItem('gre_flashcard_time');
        if (savedTime) {
          try {
            const savedTimeSeconds = parseInt(savedTime, 10);
            if (!isNaN(savedTimeSeconds) && savedTimeSeconds >= 0) {
              initialSavedTimeRef.current = savedTimeSeconds;
              setElapsedTime(savedTimeSeconds);
            }
          } catch (e) {
            console.error('Error parsing saved time:', e);
          }
        }

        const todayTimeValue = getTodayTime('flashcard');
        initialTodayTimeRef.current = todayTimeValue;
        setTodayTime(todayTimeValue);
        setTotalTime(getTotalTime());
        setTotalTodayTime(getTotalTodayTime());
        sessionStartTimeRef.current = Date.now();
        todaySessionStartRef.current = Date.now();
      } catch (e) {
        console.error('Error accessing localStorage:', e);
      }
    };

    const loadFromSupabase = async () => {
      if (!user) return;

      try {
        const [
          { data: answersData, error: answersError },
          { data: reportsData, error: reportsError },
          { data: progressData, error: progressError }
        ] = await Promise.all([
          dataService.flashcardAnswers.getAll(user.id),
          dataService.reportedIssues.getByGameMode(user.id, 'flashcard'),
          dataService.userProgressState.get(user.id, 'flashcard', 'last_word_index')
        ]);

        if (!isMounted) return;

        if (answersError) {
          console.error('Error loading flashcard answers from Supabase:', answersError.message);
        } else if (answersData) {
          const supaCorrect: AnswerRecord[] = [];
          const supaIncorrect: AnswerRecord[] = [];

          answersData.forEach((record: any) => {
            const answerRecord: AnswerRecord = {
              word: record.word,
              definition: record.definition || '',
              isCorrect: !!record.correct
            };
            if (record.correct) {
              supaCorrect.push(answerRecord);
            } else {
              supaIncorrect.push(answerRecord);
            }
          });

          setCorrectAnswers(supaCorrect);
          setIncorrectAnswers(supaIncorrect);
        }

        if (reportsError) {
          console.error('Error loading reported issues from Supabase:', reportsError.message);
        } else if (reportsData) {
          const mappedReports: ReportRecord[] = reportsData.map((issue: any) => ({
            id: issue.id,
            word: issue.word,
            type: 'definition',
            note: issue.note || undefined,
            timestamp: issue.timestamp || Date.now()
          }));
          setReportedIssues(mappedReports);
          try {
            localStorage.setItem('gre_reported_issues', JSON.stringify(mappedReports));
          } catch (e) {
            console.error('Error saving reported issues to localStorage:', e);
          }
        }

        if (progressError) {
          console.error('Error loading progress state from Supabase:', progressError.message);
        } else if (progressData && progressData.state_value) {
          const index = parseInt(progressData.state_value, 10);
          if (!isNaN(index) && index >= 0) {
            setPendingWordIndex(index);
          }
        }

        // refresh total times after Supabase load
        setTotalTime(getTotalTime());
        setTotalTodayTime(getTotalTodayTime());
      } catch (e) {
        console.error('Unexpected error loading Supabase data:', e);
      }
    };

    loadFromLocalStorage();
    if (user) {
      loadFromSupabase();
    }

    return () => {
      isMounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isFastAttack]);

  // Timer effect - update every second
  useEffect(() => {
    if (!sessionStartTimeRef.current) {
      sessionStartTimeRef.current = Date.now();
    }
    if (!todaySessionStartRef.current) {
      todaySessionStartRef.current = Date.now();
    }

    const interval = setInterval(() => {
      if (sessionStartTimeRef.current && todaySessionStartRef.current) {
        const now = Date.now();
        
        // Mode-specific timer - use initial saved time + current session elapsed
        const sessionElapsed = Math.floor((now - sessionStartTimeRef.current) / 1000);
        const totalElapsed = initialSavedTimeRef.current + sessionElapsed;
        setElapsedTime(totalElapsed);
        
        // Today's timer - use initial saved today time + current session elapsed
        const todaySessionElapsed = Math.floor((now - todaySessionStartRef.current) / 1000);
        const totalTodayElapsed = initialTodayTimeRef.current + todaySessionElapsed;
        setTodayTime(totalTodayElapsed);
        
        // Save to localStorage every 5 seconds
        if (sessionElapsed % 5 === 0 && sessionElapsed > 0) {
          try {
            localStorage.setItem('gre_flashcard_time', totalElapsed.toString());
            saveTodayTime('flashcard', totalTodayElapsed);
            // Update initial saved time to the new total, then reset session start
            initialSavedTimeRef.current = totalElapsed;
            initialTodayTimeRef.current = totalTodayElapsed;
            sessionStartTimeRef.current = Date.now();
            todaySessionStartRef.current = Date.now();
          } catch (e) {
            console.error('Error saving time to localStorage:', e);
          }
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);


  // Save both answers arrays to localStorage whenever they change
  // Skip saving on initial mount (when loading from localStorage)
  useEffect(() => {
    // Skip saving on initial mount
    if (isInitialMount) {
      setIsInitialMount(false);
      return;
    }
    
    try {
      localStorage.setItem('gre_correct_answers', JSON.stringify(correctAnswers));
      localStorage.setItem('gre_incorrect_answers', JSON.stringify(incorrectAnswers));
    } catch (e) {
      console.error('Error saving to localStorage:', e);
    }
  }, [correctAnswers, incorrectAnswers, isInitialMount]);

  // Save current word index to localStorage / Supabase when the card changes.
  // Intentionally omit `user` from deps — login must not trigger a save that
  // races with and overwrites the Supabase read happening in loadFromSupabase.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (words.length > 0 && currentWordIndex >= 0) {
      try {
        localStorage.setItem('gre_last_word_index', currentWordIndex.toString());
      } catch (e) {
        console.error('Error saving word index to localStorage:', e);
      }

      if (user) {
        dataService.userProgressState
          .upsert({
            user_id: user.id,
            game_mode: 'flashcard',
            state_key: 'last_word_index',
            state_value: currentWordIndex.toString()
          })
          .catch(e => console.error('Error saving word index to Supabase:', e));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWordIndex, words.length]);

  // Effect 1: Load base vocab words once — empty deps so auth flicker never reruns this
  useEffect(() => {
    if (providedWords && providedWords.length > 0) {
      setWords(providedWords);
      setLoading(false);
      return;
    }

    let isMounted = true;
    setLoading(true);
    fetchAllVocabWords()
      .then(({ data, error }) => {
        if (!isMounted) return;
        if (error) {
          console.error('Error loading vocab from Supabase:', error.message);
          setLoading(false);
          return;
        }
        setWords(mergeAndSortWords([], data ?? []));
        setLoading(false);
      })
      .catch(err => {
        if (!isMounted) return;
        console.error('Unexpected error loading vocab:', err);
        setLoading(false);
      });
    return () => { isMounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Populate allWordsRef with the full vocab bank for diverse distractors.
  // In FastAttack mode words is only 30 words, so we fetch the full list separately.
  // fetchAllVocabWords is a cached singleton — this is essentially free on repeated calls.
  useEffect(() => {
    fetchAllVocabWords().then(({ data }) => {
      if (data && data.length > 0) allWordsRef.current = data;
    });
  }, []);

  // Effect 2: Merge custom words when user is known
  useEffect(() => {
    if (!user) {
      setCustomWordEntries([]);
      setShowOnlyCustom(false);
      return;
    }

    let isMounted = true;
    dataService.customWords.list(user.id)
      .then(({ data: customData, error: customError }) => {
        if (!isMounted) return;
        if (customError) {
          console.error('Error loading custom words from Supabase:', customError.message);
          return;
        }
        if (customData) {
          const customList = customData.map(mapCustomRowToWordData);
          setCustomWordEntries(customList);
          setWords(prev => mergeAndSortWords(customList, prev.filter(w => !w.isCustom)));
        }
      })
      .catch(e => {
        if (!isMounted) return;
        console.error('Error loading custom words:', e);
      });
    return () => { isMounted = false; };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply pending word index once words are loaded (used for both regular and fast attack resume)
  useEffect(() => {
    if (pendingWordIndex !== null && words.length > 0) {
      const clamped = Math.min(pendingWordIndex, words.length - 1);
      setCurrentWordIndex(clamped);
      setPendingWordIndex(null);
    }
  }, [words.length, pendingWordIndex]);

  // Fast Attack: auto-start session (skip the "Session N" idle screen)
  useEffect(() => {
    if (isFastAttack && !isSessionActive) {
      startSession();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFastAttack]);

  // Fast Attack: restore saved session progress on mount
  // Props (from Supabase via parent) take priority; localStorage is the same-device fallback
  useEffect(() => {
    if (!isFastAttack) return;
    // Prop-based restore (multi-device, from Supabase)
    if (initialCardIndex !== undefined) {
      setPendingWordIndex(initialCardIndex);
    } else if (progressKey) {
      // Same-device localStorage fallback
      try {
        const savedIndex = localStorage.getItem(`gre_fa_${progressKey}_index`);
        if (savedIndex) setPendingWordIndex(parseInt(savedIndex, 10));
      } catch {}
    }
    const words = initialAnsweredWords ?? (() => {
      if (!progressKey) return null;
      try {
        const s = localStorage.getItem(`gre_fa_${progressKey}_answered`);
        return s ? JSON.parse(s) as string[] : null;
      } catch { return null; }
    })();
    if (words && words.length > 0) {
      fastAttackAnsweredRef.current = new Set(words);
      setFastAttackAnsweredCount(words.length);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fast Attack: save card index whenever it changes
  useEffect(() => {
    if (!isFastAttack) return;
    if (progressKey) {
      localStorage.setItem(`gre_fa_${progressKey}_index`, String(currentWordIndex));
    }
    onCardProgress?.(currentWordIndex, Array.from(fastAttackAnsweredRef.current));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWordIndex]);

  // Fast Attack: save answered set whenever a new word is answered
  useEffect(() => {
    if (!isFastAttack) return;
    if (progressKey) {
      localStorage.setItem(
        `gre_fa_${progressKey}_answered`,
        JSON.stringify(Array.from(fastAttackAnsweredRef.current))
      );
    }
    onCardProgress?.(currentWordIndex, Array.from(fastAttackAnsweredRef.current));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fastAttackAnsweredCount]);

  // Get words to use - either all words or only incorrect ones in review mode
  const basePool = useMemo(() => {
    let pool = showOnlyCustom ? words.filter(word => word.isCustom) : words;
    if (drillWords.length > 0) {
      pool = pool.filter(w => drillWords.includes(w.Word));
    }
    if (reviewMode && incorrectAnswers.length > 0) {
      const incorrectWords = incorrectAnswers.map(record => record.word);
      pool = pool.filter(word => incorrectWords.includes(word.Word));
    } else {
      pool = [...pool];
    }
    // Free users: cap at FREE_WORD_LIMIT words (not applied in Fast Attack mode
    // since words are already pre-filtered to the day's group)
    if (!isPro && !isFastAttack && !showOnlyCustom && !reviewMode) {
      pool = pool.slice(0, FREE_WORD_LIMIT);
    }
    return pool;
  }, [reviewMode, incorrectAnswers, words, showOnlyCustom, isPro, isFastAttack, drillWords]);

  useEffect(() => {
    if (!isShuffled) {
      setShuffleOrder(null);
      return;
    }
    // regenerate order if pool length changes
    const length = basePool.length;
    if (!length) {
      setShuffleOrder(null);
      return;
    }
    // if existing order matches length, keep it
    if (shuffleOrder && shuffleOrder.length === length) return;
    const order = Array.from({ length }, (_, i) => i);
    for (let i = order.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [order[i], order[j]] = [order[j], order[i]];
    }
    setShuffleOrder(order);
  }, [isShuffled, basePool.length, shuffleOrder]);

  const wordsToUse = useMemo(() => {
    if (isShuffled && shuffleOrder && shuffleOrder.length === basePool.length) {
      return shuffleOrder.map(idx => basePool[idx]);
    }
    return basePool;
  }, [isShuffled, shuffleOrder, basePool]);

  // Initialise distractor pool whenever the word list changes.
  // Always prefer the full vocab bank so FastAttack groups (30 words)
  // still get distractors drawn from the entire 1100+ word list.
  useEffect(() => {
    if (wordsToUse.length === 0) return;
    const source = allWordsRef.current.length > wordsToUse.length
      ? allWordsRef.current
      : wordsToUse;
    const pool = [...source].sort(() => Math.random() - 0.5).map(w => w.Word);
    distractorPoolRef.current = pool;
    distractorIndexRef.current = 0;
    recentCorrectRef.current = [];
  }, [wordsToUse]);

  // Keep a stable ref to onComplete so the effect below doesn't re-run
  // every time FastAttackGame re-renders (its 1-second timer creates a new fn reference).
  const onCompleteRef = useRef(onComplete);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  // Auto-complete when every word in the fast attack set has been answered once
  useEffect(() => {
    if (!isFastAttack || wordsToUse.length === 0) return;
    if (fastAttackAnsweredCount >= wordsToUse.length) {
      const cb = onCompleteRef.current;
      const timer = setTimeout(() => cb?.(), 800);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fastAttackAnsweredCount, wordsToUse.length, isFastAttack]);

  useEffect(() => {
    if (showOnlyCustom && customWordEntries.length === 0) {
      setShowOnlyCustom(false);
      return;
    }

    if (wordsToUse.length > 0) {
      setCurrentWordIndex(prev => Math.min(prev, wordsToUse.length - 1));
    } else {
      setCurrentWordIndex(0);
    }
  }, [showOnlyCustom, customWordEntries.length, wordsToUse.length]);

  // Generate multiple choice options
  const options = useMemo(() => {
    if (wordsToUse.length === 0 || !wordsToUse[currentWordIndex]) return [];

    // If we already have frozen options and haven't changed words, use them
    if (frozenOptions.length > 0 && showResult) {
      return frozenOptions;
    }

    const currentWord = wordsToUse[currentWordIndex];
    const correctAnswer = currentWord.Definition;

    // Helper: pick next distractor word (by word string) from the pool
    const getNextDistractorWord = (exclude: string[]): string | null => {
      const pool = distractorPoolRef.current;
      if (pool.length === 0) return null;
      const recent = recentCorrectRef.current;
      let attempts = 0;
      while (attempts < pool.length) {
        const candidate = pool[distractorIndexRef.current % pool.length];
        distractorIndexRef.current++;
        if (attempts === pool.length - 1) distractorIndexRef.current = 0;
        if (!exclude.includes(candidate) && !recent.includes(candidate)) {
          return candidate;
        }
        attempts++;
      }
      // Fallback: any word not in exclude
      return pool.find(w => !exclude.includes(w)) ?? pool[0];
    };

    // Build 3 wrong definitions via the pool
    const wrongOptions: string[] = [];
    const excludedWords = [currentWord.Word];

    while (wrongOptions.length < 3) {
      const distractorWord = getNextDistractorWord(excludedWords);
      if (distractorWord == null) break;
      excludedWords.push(distractorWord);
      const match = wordsToUse.find(w => w.Word === distractorWord)
        ?? words.find(w => w.Word === distractorWord)
        ?? allWordsRef.current.find(w => w.Word === distractorWord);
      if (match && match.Definition !== correctAnswer) {
        wrongOptions.push(match.Definition);
      }
    }

    // Combine and shuffle
    const allOptions = [correctAnswer, ...wrongOptions];
    for (let i = allOptions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [allOptions[i], allOptions[j]] = [allOptions[j], allOptions[i]];
    }

    return allOptions;
  }, [wordsToUse, currentWordIndex, words, frozenOptions, showResult]);

  const handleFetchDefinition = async () => {
    setCustomWordError(null);
    setCustomWordSuccess(null);

    if (!newWord.trim()) {
      setCustomWordError('Enter a word to look up.');
      return;
    }

    setLookupLoading(true);
    try {
      const definition = await fetchDefinitionForWord(newWord);
      setNewDefinition(definition);
    } catch (error: any) {
      setCustomWordError(error?.message || 'Could not find a definition.');
    } finally {
      setLookupLoading(false);
    }
  };

  const handleAddCustomWord = async () => {
    setCustomWordError(null);
    setCustomWordSuccess(null);

    if (!user) {
      setCustomWordError('Sign in to add custom words.');
      return;
    }

    if (!newWord.trim() || !newDefinition.trim()) {
      setCustomWordError('Word and definition are required.');
      return;
    }

    setSavingCustomWord(true);
    try {
      const payload = {
        user_id: user.id,
        word: newWord.trim(),
        definition: newDefinition.trim(),
        similar_words_category: newSynonyms.trim() || undefined,
        similar_words: newSynonyms.trim() || undefined,
        source: 'user'
      };

      const { data, error } = await dataService.customWords.insert(payload);
      if (error) {
        throw new Error(error.message);
      }
      if (data) {
        const mapped = mapCustomRowToWordData(data);
        setCustomWordEntries(prev => [mapped, ...prev]);
        setWords(prev => mergeAndSortWords([mapped], prev));
        setCustomWordSuccess('Word added!');
        setNewWord('');
        setNewDefinition('');
        setNewSynonyms('');
      }
    } catch (err: any) {
      setCustomWordError(err?.message || 'Failed to add word. Please try again.');
    } finally {
      setSavingCustomWord(false);
    }
  };

  const handleAnswerSelect = (optionIndex: number) => {
    if (showResult) return;
    
    // Save current scroll position before state updates
    scrollPositionRef.current = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
    userScrolledRef.current = false; // Reset flag when new answer is selected
    
    // Freeze the current options array so it doesn't change
    setFrozenOptions([...options]);
    
    const selectedOptionText = options[optionIndex];
    setSelectedAnswer(optionIndex);
    setSelectedAnswerText(selectedOptionText);
    setShowResult(true);
    
    const currentWord = wordsToUse[currentWordIndex];
    const isCorrect = selectedOptionText === currentWord.Definition;
    
    const answerRecord: AnswerRecord = {
      word: currentWord.Word,
      definition: currentWord.Definition,
      isCorrect
    };
    
    // Use functional updates to ensure we have the latest state
    if (isCorrect) {
      recordCardSeen(currentWord.Word);
      recentCorrectRef.current = [currentWord.Word, ...recentCorrectRef.current].slice(0, 3);
      setCorrectAnswers(prev => {
        // Check if word already exists in correct answers, if so don't add duplicate
        const exists = prev.some(record => record.word === currentWord.Word);
        if (exists) return prev;
        return [...prev, answerRecord];
      });
      setIncorrectAnswers(prev => prev.filter(record => record.word !== currentWord.Word));
    } else {
      setIncorrectAnswers(prev => {
        const exists = prev.some(record => record.word === currentWord.Word);
        if (exists) return prev;
        return [...prev, answerRecord];
      });
      setCorrectAnswers(prev => prev.filter(record => record.word !== currentWord.Word));
    }

    // Track unique words answered in this fast attack session
    if (isFastAttack && !fastAttackAnsweredRef.current.has(currentWord.Word)) {
      fastAttackAnsweredRef.current.add(currentWord.Word);
      setFastAttackAnsweredCount(prev => prev + 1);
    }

    // Award XP immediately per answer (avoids stale closure on unmount)
    if (user) {
      supabase.rpc('record_session', {
        p_user_id: user.id,
        p_xp_earned: isCorrect ? 1 : 0,
        p_correct: isCorrect ? 1 : 0,
        p_answered: 1,
      }).then(({ error }) => {
        if (error) console.error('[XP] record_session failed:', error.message, error);
        else if (isCorrect) console.log('[XP] +1 awarded to', user.id);
      });
    } else {
      console.warn('[XP] skipped — user is null');
    }

    saveAnswerToSupabase(currentWord, isCorrect);
  };

  // End session on unmount
  useEffect(() => {
    return () => {
      if (isSessionActive) {
        endSession(0);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSessionActive, endSession]);

  // If the page jumps when showing the result, force-scroll back to the stored position
  useEffect(() => {
    if (!showResult) return;
    const y = scrollPositionRef.current;
    requestAnimationFrame(() => {
      window.scrollTo({ top: y, behavior: 'auto' });
    });
  }, [showResult]);

  // Track user scroll to prevent auto-scroll back
  useEffect(() => {
    const handleScroll = () => {
      if (showResult) {
        userScrolledRef.current = true;
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [showResult]);

  // Removed scroll restoration to prevent auto-scrolling to top

  const nextWord = () => {
    setIsSwiping(false);
    setSwipeOffsetY(-window.innerHeight * 0.4); // animate card upward
    setTimeout(() => {
      setCurrentWordIndex((prevIndex) => (prevIndex + 1) % wordsToUse.length);
      setSelectedAnswer(null);
      setSelectedAnswerText(null);
      setShowResult(false);
      setFrozenOptions([]);
      setSwipeOffsetY(0);
      setNextCardIndex(null);
      scrollPositionRef.current = 0;
      userScrolledRef.current = false;
    }, 250);
  };

  const previousWord = () => {
    setIsSwiping(false);
    setSwipeOffsetY(window.innerHeight * 0.4); // animate card downward
    setTimeout(() => {
      setCurrentWordIndex((prevIndex) => (prevIndex === 0 ? wordsToUse.length - 1 : prevIndex - 1));
      setSelectedAnswer(null);
      setSelectedAnswerText(null);
      setShowResult(false);
      setFrozenOptions([]);
      setSwipeOffsetY(0);
      setNextCardIndex(null);
      scrollPositionRef.current = 0;
      userScrolledRef.current = false;
    }, 250);
  };

  const jumpToCard = () => {
    const cardNumber = parseInt(jumpToNumber, 10);
    if (!isNaN(cardNumber) && cardNumber >= 1 && cardNumber <= wordsToUse.length) {
      setCurrentWordIndex(cardNumber - 1); // Convert to 0-based index
      setSelectedAnswer(null);
      setSelectedAnswerText(null);
      setShowResult(false);
      setFrozenOptions([]); // Clear frozen options
      setSwipeOffsetY(0);
      setNextCardIndex(null);
      setIsSwiping(false);
      setJumpToNumber('');
      // Reset scroll position reference and user scroll flag
      scrollPositionRef.current = 0;
      userScrolledRef.current = false;
      // Scroll to top smoothly when jumping
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  const handleJumpInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      jumpToCard();
    }
  };

  const handleReportDefinition = () => {
    setShowReportModal(true);
    setReportNote('');
  };

  const submitReport = async () => {
    const word = wordsToUse[currentWordIndex];
    if (!word) return;
    
    const report: ReportRecord = {
      word: word.Word,
      type: 'definition',
      reportedDefinition: word.Definition,
      note: reportNote.trim(),
      timestamp: Date.now()
    };

    const updatedReports = [...reportedIssues, report];
    setReportedIssues(updatedReports);
    
    try {
      localStorage.setItem('gre_reported_issues', JSON.stringify(updatedReports));
    } catch (e) {
      console.error('Error saving report to localStorage:', e);
    }

    if (user) {
      try {
        await dataService.reportedIssues.insert({
          user_id: user.id,
          game_mode: 'flashcard',
          word_id: word.ID || word.Word,
          word: word.Word,
          note: reportNote.trim() || undefined,
          timestamp: report.timestamp,
          date: getTodayKey()
        });
      } catch (e) {
        console.error('Error saving report to Supabase:', e);
      }
    }

    setShowReportModal(false);
    setReportNote('');
    alert('Thank you for reporting! Your feedback has been saved.');
  };

  // Reset to first word when entering/exiting review mode.
  // Intentionally omit wordsToUse.length — initial word load must not reset
  // the restored position (it would override the saved last-card index).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (wordsToUse.length > 0) {
      setCurrentWordIndex(0);
      setSelectedAnswer(null);
      setSelectedAnswerText(null);
      setShowResult(false);
      setFrozenOptions([]);
      setSwipeOffsetY(0);
      setNextCardIndex(null);
      setIsSwiping(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewMode]);

  // Clear frozen options when word changes (but not when result is shown)
  useEffect(() => {
    if (!showResult) {
      setFrozenOptions([]);
    }
  }, [currentWordIndex, showResult]);

  // Swipe gesture handlers — vertical (TikTok-style): swipe up = next, swipe down = previous
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    setIsSwiping(true);
    setSwipeOffsetY(0);
    setNextCardIndex(null);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStartX.current || !touchStartY.current) return;

    touchEndX.current = e.touches[0].clientX;
    touchEndY.current = e.touches[0].clientY;

    const deltaX = touchEndX.current - touchStartX.current;
    const deltaY = touchEndY.current - touchStartY.current;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);

    // Only activate if vertical movement dominates and threshold crossed
    if (absDeltaY > 10 && absDeltaY > absDeltaX) {
      e.preventDefault(); // stop page scroll during card swipe
      setSwipeOffsetY(deltaY);

      if (deltaY < 0) {
        // Swiping up — preview next card
        const nextIndex = (currentWordIndex + 1) % wordsToUse.length;
        setNextCardIndex(nextIndex);
      } else {
        // Swiping down — preview previous card
        const prevIndex = currentWordIndex === 0 ? wordsToUse.length - 1 : currentWordIndex - 1;
        setNextCardIndex(prevIndex);
      }
    }
  };

  const handleTouchEnd = () => {
    if (!touchStartY.current || !touchEndY.current) {
      setIsSwiping(false);
      setSwipeOffsetY(0);
      setNextCardIndex(null);
      return;
    }

    const deltaX = touchEndX.current !== null && touchStartX.current !== null
      ? touchEndX.current - touchStartX.current : 0;
    const deltaY = touchEndY.current - touchStartY.current;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);

    if (absDeltaY > 50 && absDeltaY > absDeltaX) {
      if (deltaY < 0) {
        nextWord();       // swipe up → next
      } else {
        previousWord();   // swipe down → previous
      }
    } else {
      setIsSwiping(false);
      setSwipeOffsetY(0);
      setNextCardIndex(null);
    }

    touchStartX.current = null;
    touchStartY.current = null;
    touchEndX.current = null;
    touchEndY.current = null;
  };

  const getOptionStyle = (index: number): React.CSSProperties => {
    const currentWord = wordsToUse[currentWordIndex];
    const optionsToUse = frozenOptions.length > 0 ? frozenOptions : options;
    const option = optionsToUse[index];
    const isCorrect = showResult && option === currentWord.Definition;
    const isSelected = selectedAnswer === index;
    const isWrongSelection = showResult && isSelected && !isCorrect;

    const baseStyle: React.CSSProperties = {
      ...answerButtonBase,
      backgroundColor: isSelected && !showResult ? PRIMARY_LIGHT : '#fff',
      borderColor: isSelected && !showResult ? 'var(--color-confirmation)' : BORDER,
      color: 'var(--color-text)',
    };

    if (isCorrect) {
      return {
        ...baseStyle,
        backgroundColor: 'var(--color-success-light)',
        borderColor: 'var(--color-confirmation)',
        color: 'var(--color-success-text)',
      };
    }

    if (isWrongSelection) {
      return {
        ...baseStyle,
        backgroundColor: 'var(--color-wrong-light)',
        borderColor: 'var(--color-wrong-border)',
        color: 'var(--color-danger-text)',
      };
    }

    return baseStyle;
  };

  if (loading && words.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>Loading words...</div>
        <div style={{ marginTop: '10px', fontSize: '14px', color: 'var(--color-text-muted)' }}>
          Please wait while we load the vocabulary data.
        </div>
      </div>
    );
  }

  if (words.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div style={{ color: 'var(--color-danger)', fontSize: '18px', marginBottom: '10px' }}>
          No words loaded.
        </div>
        <div style={{ fontSize: '14px', color: 'var(--color-text-muted)' }}>
          Please check:
          <ul style={{ textAlign: 'left', display: 'inline-block', marginTop: '10px' }}>
            <li>The words.csv file exists in the public folder</li>
            <li>Check the browser console for errors</li>
            <li>Try refreshing the page</li>
          </ul>
        </div>
      </div>
    );
  }

  // Idle screen — shown before session starts (after words are loaded)
  if (!isSessionActive) {
    return (
      <div style={{ padding: '24px 16px', fontFamily: 'inherit' }}>
        <div style={{
          backgroundColor: '#1F4C40',
          borderRadius: '20px',
          padding: '32px 24px',
          textAlign: 'center',
          color: '#FFFFFF',
        }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>⚡</div>
          <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>
            Session {sessionNumber}
          </div>
          {lastSessionCardCount > 0 && (
            <div style={{ fontSize: 13, color: '#9FE1CB', marginBottom: 16 }}>
              Last session: {lastSessionCardCount} cards
            </div>
          )}
          <div style={{ fontSize: 13, color: '#9FE1CB', marginBottom: 24 }}>
            {lifetimeSeenCount} / {wordsToUse.length} words seen lifetime
          </div>
          <button
            onClick={startSession}
            style={{
              backgroundColor: '#FFFFFF',
              color: '#1F4C40',
              border: 'none',
              borderRadius: 999,
              padding: '14px 40px',
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
              width: '100%',
            }}
          >
            Start session {sessionNumber}
          </button>
        </div>
      </div>
    );
  }

  const currentWord = wordsToUse[currentWordIndex];
  const totalAnswered = correctAnswers.length + incorrectAnswers.length;
  const accuracy = totalAnswered > 0 ? Math.round((correctAnswers.length / totalAnswered) * 100) : 0;
  // Calculate percentage complete: lifetime unique words seen out of total words (capped at 100%)
  const progressPercent = wordsToUse.length > 0 ? Math.min(100, Math.round((lifetimeSeenCount / wordsToUse.length) * 100)) : 0;
  const percentageComplete = progressPercent;

  if (showBank) {
    return (
      <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
          <h1 style={{ margin: 0 }}>Answer Bank</h1>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={handleClearData} className="btn-danger">
              Clear Data
            </button>
            <button onClick={() => setShowBank(false)} className="btn-secondary">
              Back to Quiz
            </button>
          </div>
        </div>

        <div style={{ marginBottom: '30px', display: 'flex', gap: '20px', justifyContent: 'center' }}>
          <div style={{
            padding: '15px',
            backgroundColor: 'var(--color-success-light)',
            borderRadius: '12px',
            flex: 1,
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 10px 0', color: 'var(--color-success-text)' }}>Correct ✓</h3>
            <p style={{ fontSize: '24px', margin: 0, fontWeight: 'bold', color: 'var(--color-success-text)' }}>
              {correctAnswers.length}
            </p>
          </div>
          <div style={{
            padding: '15px',
            backgroundColor: 'var(--color-danger-light)',
            borderRadius: '12px',
            flex: 1,
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 10px 0', color: 'var(--color-danger-text)' }}>Incorrect ✗</h3>
            <p style={{ fontSize: '24px', margin: 0, fontWeight: 'bold', color: 'var(--color-danger-text)' }}>
              {incorrectAnswers.length}
            </p>
          </div>
          <div style={{
            padding: '15px',
            backgroundColor: 'var(--color-surface)',
            borderRadius: '12px',
            flex: 1,
            textAlign: 'center',
            border: '1px solid var(--color-border)'
          }}>
            <h3 style={{ margin: '0 0 10px 0', color: 'var(--color-text)' }}>Accuracy</h3>
            <p style={{ fontSize: '24px', margin: 0, fontWeight: 'bold', color: 'var(--color-primary)' }}>
              {accuracy}%
            </p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div>
            <h2 style={{ color: 'var(--color-success-text)', marginBottom: '15px' }}>Correct Answers ({correctAnswers.length})</h2>
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {correctAnswers.map((record, idx) => (
                <div key={idx} style={{
                  padding: '15px',
                  marginBottom: '10px',
                  backgroundColor: 'var(--color-success-light)',
                  borderRadius: '12px',
                  border: '1px solid var(--color-success)',
                  borderLeftWidth: '4px'
                }}>
                  <strong style={{ color: 'var(--color-success-text)' }}>{record.word}</strong>
                  <p style={{ margin: '5px 0 0 0', color: 'var(--color-success-text)', fontSize: '14px' }}>
                    {record.definition}
                  </p>
                </div>
              ))}
              {correctAnswers.length === 0 && (
                <p style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>No correct answers yet</p>
              )}
            </div>
          </div>

          <div>
            <h2 style={{ color: 'var(--color-danger-text)', marginBottom: '15px' }}>Incorrect Answers ({incorrectAnswers.length})</h2>
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {incorrectAnswers.map((record, idx) => (
                <div key={idx} style={{
                  padding: '15px',
                  marginBottom: '10px',
                  backgroundColor: 'var(--color-danger-light)',
                  borderRadius: '12px',
                  border: '1px solid var(--color-danger)',
                  borderLeftWidth: '4px'
                }}>
                  <strong style={{ color: 'var(--color-danger-text)' }}>{record.word}</strong>
                  <p style={{ margin: '5px 0 0 0', color: 'var(--color-danger-text)', fontSize: '14px' }}>
                    {record.definition}
                  </p>
                </div>
              ))}
              {incorrectAnswers.length === 0 && (
                <p style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>No incorrect answers yet</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If in review mode but no incorrect answers or no words to use, show message
  if (reviewMode && (incorrectAnswers.length === 0 || wordsToUse.length === 0)) {
    return (
      <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
        <h1 style={{ marginBottom: '20px' }}>Review Mode</h1>
        <div style={{
          padding: '20px',
          backgroundColor: 'var(--color-success-light)',
          borderRadius: '12px',
          marginBottom: '20px',
          border: '1px solid var(--color-success)'
        }}>
          <p style={{ fontSize: '18px', color: 'var(--color-success-text)', margin: 0 }}>
            Great job! You have no incorrect answers to review.
          </p>
        </div>
        <button
          onClick={() => setReviewMode(false)}
          style={{
            padding: '12px 24px',
            fontSize: '16px',
            cursor: 'pointer',
            backgroundColor: 'var(--color-primary)',
            color: 'white',
            border: 'none',
            borderRadius: '999px'
          }}
        >
          Back to Regular Quiz
        </button>
      </div>
    );
  }

  const quizStats = [
    { label: 'Correct', value: correctAnswers.length, color: 'var(--color-success)' },
    { label: 'Incorrect', value: incorrectAnswers.length, color: 'var(--color-danger)' },
    { label: 'Accuracy', value: `${accuracy}%`, color: PRIMARY },
    { label: 'Progress', value: `${percentageComplete}%`, color: PRIMARY },
    { label: 'Flashcard Time', value: formatTime(elapsedTime), color: PRIMARY },
    { label: 'Today', value: formatTime(todayTime), color: PRIMARY }
  ];

  const timeOverviewStats = [
    { label: 'Total All Modes', value: formatTime(totalTime) },
    { label: 'Total Today', value: formatTime(totalTodayTime) },
    { label: 'Total Yesterday', value: formatTime(getTotalYesterdayTime()) },
    { label: 'Total This Week', value: formatTime(getTotalThisWeekTime()) }
  ];

  const renderMetricsContent = () => (
    <Accordion title={reviewMode ? 'Review Metrics' : 'Study Metrics'} defaultOpenDesktop defaultOpenMobile={false}>
      <div style={viewToggleWrapStyle}>
        <button
          onClick={() => setSelectedView('stats')}
          style={viewToggleButtonStyle(selectedView === 'stats')}
        >
          Flashcard Stats
        </button>
        <button
          onClick={() => setSelectedView('time')}
          style={viewToggleButtonStyle(selectedView === 'time')}
        >
          Time Overview
        </button>
      </div>
      {selectedView === 'stats' ? (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px'
          }}
        >
          {quizStats.map(stat => (
            <div key={stat.label} style={statPillStyle}>
              <p style={{ fontSize: '12px', color: TEXT_MUTED, margin: 0 }}>{stat.label}</p>
              <strong style={{ fontSize: '26px', fontWeight: 800, letterSpacing: '-1px', color: stat.color }}>{stat.value}</strong>
            </div>
          ))}
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '12px'
          }}
        >
          {[
            { label: 'Flashcard Time', value: formatTime(elapsedTime) },
            { label: 'Flashcard Today', value: formatTime(todayTime) },
            ...timeOverviewStats
          ].map(stat => (
            <div
              key={stat.label}
              style={{
                backgroundColor: SURFACE,
                border: `1px solid ${BORDER}`,
                borderRadius: '16px',
                padding: '12px'
              }}
            >
              <p style={{ fontSize: '12px', color: TEXT_MUTED, marginBottom: '4px' }}>{stat.label}</p>
              <strong style={{ fontSize: '16px' }}>{stat.value}</strong>
            </div>
          ))}
        </div>
      )}
      {!user && <SignInAlert />}
    </Accordion>
  );

  const nextButtonRowStyle: React.CSSProperties = isMobile
    ? {
        position: 'sticky',
        bottom: 0,
        background: 'linear-gradient(180deg, rgba(255,255,255,0), #fff 70%)',
        padding: '12px 0',
        display: 'flex',
        justifyContent: 'center',
        zIndex: 5
      }
    : { display: 'flex', justifyContent: 'center', marginTop: '16px' };


  return (
    <div style={{ backgroundColor: 'var(--color-background)', padding: '12px 16px' }}>
      {showPaywall && <PaywallModal feature="Full word bank (1,100+ words)" onClose={() => setShowPaywall(false)} />}
      <Helmet>
        <title>GRE Flashcards – 1,100+ Words | Phrenos</title>
        <meta name="description" content="Study all 1,100+ GRE vocabulary words with smart flashcards. Track wrong answers, jump to any word, and build a personal word bank. Free and mobile-friendly." />
        <meta property="og:title" content="GRE Flashcards – 1,100+ Words | Phrenos" />
        <meta property="og:description" content="Study all 1,100+ GRE vocabulary words with smart flashcards. Track wrong answers, jump to any word, and build a personal word bank." />
        <meta property="og:url" content="https://phrenos.app/" />
        <link rel="canonical" href="https://phrenos.app/" />
      </Helmet>
      <div style={{ maxWidth: '1024px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div>
          <p className="eyebrow">Vocabulary</p>
          <h1 style={{ margin: 0 }}>Flashcards</h1>
        </div>
        {!isPro && !isFastAttack && words.length > FREE_WORD_LIMIT && (
          <button
            onClick={() => setShowPaywall(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 14px', borderRadius: 12,
              backgroundColor: 'var(--color-success-light)',
              border: '1px solid var(--color-primary)',
              color: 'var(--color-primary)', fontSize: 13, fontWeight: 600,
              cursor: 'pointer', textAlign: 'left',
            }}
          >
            🔒 Free plan: first {FREE_WORD_LIMIT} words. Upgrade to unlock all {words.length}+.
          </button>
        )}
        {shouldShowMetrics && (
          <>
            {metricsPanelOpen && (
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(31,76,64,0.5)',
                  zIndex: 1000,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  padding: '20px'
                }}
                onClick={() => setMetricsPanelOpen(false)}
              >
                <div
                  style={{
                    width: '100%',
                    maxWidth: '480px',
                    backgroundColor: '#fff',
                    borderRadius: '20px',
                    padding: '20px',
                    border: `1px solid ${BORDER}`,
                    overflowY: 'auto',
                    maxHeight: '90vh'
                  }}
                  onClick={e => e.stopPropagation()}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <strong style={{ fontSize: '16px' }}>Study Metrics</strong>
                    <button
                      onClick={() => setMetricsPanelOpen(false)}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        fontSize: '20px',
                        cursor: 'pointer',
                        color: TEXT_MUTED
                      }}
                      aria-label="Close metrics panel"
                    >
                      ×
                    </button>
                  </div>
                  {renderMetricsContent()}
                </div>
              </div>
            )}
            {!metricsPanelOpen && (
              <div style={{ boxShadow: metricsPanelOpen ? 'none' : undefined }}>
                {renderMetricsContent()}
              </div>
            )}
          </>
        )}


        {focusMode && !metricsPanelOpen && isFastAttack && sessionLabel && (
          <div style={{ fontSize: '13px', color: TEXT_MUTED, marginBottom: '4px' }}>
            {sessionLabel} · {currentWordIndex + 1}/{wordsToUse.length}
          </div>
        )}

        <div
          style={{
            position: 'relative',
            margin: '16px 0',
            maxWidth: '100%',
            minHeight: '280px'
          }}
        >
        {nextCardIndex !== null && wordsToUse[nextCardIndex] && (
          <div
            style={{
              ...cardBaseStyle,
              position: 'absolute',
              inset: 0,
              transform: 'scale(0.97) translateY(10px)',
              opacity: 0.4,
              zIndex: 0
            }}
          >
            <h2 style={{ fontSize: '20px', color: TEXT_MUTED }}>
              Up next: "{wordsToUse[nextCardIndex]?.Word}"
            </h2>
          </div>
        )}

        <div
          ref={cardContainerRef}
          style={{
            ...cardBaseStyle,
            borderTop: `4px solid ${PRIMARY}`,
            position: 'relative',
            zIndex: 1,
            transform: `translateY(${swipeOffsetY}px)`,
            opacity: isSwiping ? Math.max(0.2, 1 - Math.abs(swipeOffsetY) / 250) : 1,
            transition: isSwiping ? 'none' : 'transform 0.25s ease, opacity 0.25s ease',
            touchAction: 'pan-x'
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
        {/* Word pill badge */}
        <div style={{ textAlign: 'center', marginBottom: '14px' }}>
          <span style={{
            display: 'inline-block',
            backgroundColor: PRIMARY,
            color: '#fff',
            borderRadius: '999px',
            padding: '10px 28px',
            fontSize: '22px',
            fontWeight: 800,
            letterSpacing: '-0.5px',
          }}>
            {currentWord?.Word}
          </span>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', gap: '12px' }}>
          <p style={{ fontSize: '15px', color: TEXT_MUTED, margin: 0 }}>What does this word mean?</p>
          <button
            onClick={handleReportDefinition}
            style={{
              padding: '5px 10px',
              fontSize: '12px',
              borderRadius: '999px',
              border: `1px solid ${BORDER}`,
              backgroundColor: SURFACE_MUTED,
              color: 'var(--color-danger)',
              cursor: 'pointer',
              flexShrink: 0,
            }}
            title="Report incorrect definition"
          >
            Report
          </button>
        </div>

        <div style={{ marginTop: '8px' }}>
          {options.map((option, index) => (
            <button
              key={index}
              onClick={() => handleAnswerSelect(index)}
              style={{
                ...getOptionStyle(index),
                marginBottom: '10px'
              }}
              disabled={showResult}
            >
              <span style={{
                width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                border: '1.5px solid var(--color-border)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '11px', fontWeight: 700, color: 'var(--color-text-muted)',
                transition: 'all 0.15s ease',
              }}>
                {['A', 'B', 'C', 'D'][index]}
              </span>
              <span style={{ flex: 1 }}>{option}</span>
            </button>
          ))}
        </div>

        {/* Drill mode: "Got it" removes word from drill list */}
        {isDrillMode && drillWords.includes(wordsToUse[currentWordIndex]?.Word) && (
          <button
            onClick={async () => {
              const word = wordsToUse[currentWordIndex].Word;
              if (user) await removeDrillWord(user.id, word);
              setDrillWords(prev => prev.filter(w => w !== word));
            }}
            style={{
              width: '100%', marginTop: 10,
              padding: '12px', borderRadius: 14,
              backgroundColor: 'var(--color-success-light)',
              border: '1.5px solid var(--color-primary)',
              color: 'var(--color-primary)', fontSize: 14, fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Got it ✓ — remove from drill list
          </button>
        )}

        {/* Card footer: session info + jump link */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '10px',
          fontSize: '11px',
          color: TEXT_MUTED,
        }}>
          <span>
            {isFastAttack && sessionLabel
              ? `${sessionLabel} · ${currentWordIndex + 1}/${wordsToUse.length}`
              : `Session ${Math.floor(currentWordIndex / SESSION_SIZE) + 1} · ${(currentWordIndex % SESSION_SIZE) + 1}/${SESSION_SIZE}`}
            {reviewMode && <span style={{ color: 'var(--color-danger)', marginLeft: 6 }}>· Review</span>}
          </span>
          <button
            onClick={() => setShowJumpSection(v => !v)}
            style={{ fontSize: '11px', color: PRIMARY, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
          >
            {showJumpSection ? 'Hide jump ↑' : 'Jump to card ↓'}
          </button>
        </div>

        {/* Collapsible jump input */}
        {showJumpSection && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px', flexWrap: 'wrap' }}>
            <input
              id="jump-to-input"
              type="number"
              min="1"
              max={wordsToUse.length}
              value={jumpToNumber}
              onChange={(e) => setJumpToNumber(e.target.value)}
              onKeyPress={handleJumpInputKeyPress}
              placeholder={`1–${wordsToUse.length}`}
              style={{
                padding: '6px 10px',
                fontSize: '13px',
                width: '90px',
                border: `1px solid ${BORDER}`,
                borderRadius: '10px',
                textAlign: 'center',
                background: '#fff',
              }}
            />
            <button
              onClick={jumpToCard}
              disabled={
                !jumpToNumber ||
                isNaN(parseInt(jumpToNumber, 10)) ||
                parseInt(jumpToNumber, 10) < 1 ||
                parseInt(jumpToNumber, 10) > wordsToUse.length
              }
              style={{
                padding: '6px 14px',
                fontSize: '13px',
                borderRadius: '999px',
                border: 'none',
                backgroundColor: PRIMARY,
                color: '#fff',
                cursor: 'pointer',
                opacity: (!jumpToNumber || isNaN(parseInt(jumpToNumber, 10)) || parseInt(jumpToNumber, 10) < 1 || parseInt(jumpToNumber, 10) > wordsToUse.length) ? 0.4 : 1,
                transition: 'opacity 0.15s ease',
              }}
            >
              Go
            </button>
          </div>
        )}

        {showResult && (
          <>
            <div style={{
              marginTop: '16px',
              padding: '12px',
              borderRadius: '12px',
              borderLeft: '5px solid',
              backgroundColor: selectedAnswerText !== null && selectedAnswerText === currentWord.Definition
                ? 'var(--color-success-light)'
                : 'var(--color-danger-light)',
              borderColor: selectedAnswerText !== null && selectedAnswerText === currentWord.Definition
                ? 'var(--color-success)'
                : 'var(--color-danger)',
              color: selectedAnswerText !== null && selectedAnswerText === currentWord.Definition
                ? 'var(--color-success-text)'
                : 'var(--color-danger-text)'
            }}>
              {selectedAnswerText !== null && selectedAnswerText === currentWord.Definition ? (
                <p style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>✓ Correct!</p>
              ) : (
                <p style={{ margin: 0, fontSize: '14px', fontWeight: 'bold' }}>✗ Incorrect. The correct answer was: {currentWord.Definition}</p>
              )}
            </div>
            <div style={nextButtonRowStyle}>
              <button 
                onClick={nextWord}
                style={{
                  padding: '12px 28px',
                  fontSize: '16px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  backgroundColor: 'var(--color-success)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '999px',
                  transition: 'transform 0.2s ease, background 0.2s ease'
                }}
              >
                Next Word →
              </button>
            </div>
          </>
        )}
        </div>
      </div>

      {/* Buttons below cards — primary 2-col grid + collapsible secondary */}
      <div style={{ marginTop: '16px' }}>
        {/* Primary actions */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
          {incorrectAnswers.length > 0 && (
            <button
              onClick={() => setReviewMode(!reviewMode)}
              style={{
                padding: '12px 16px',
                fontSize: '14px',
                cursor: 'pointer',
                backgroundColor: reviewMode ? 'var(--color-success)' : 'var(--color-danger)',
                color: '#fff',
                border: 'none',
                borderRadius: '999px',
                fontWeight: 700,
                minHeight: 48,
              }}
            >
              {reviewMode ? 'Exit Review' : `Review Wrong (${incorrectAnswers.length})`}
            </button>
          )}
          <button
            onClick={() => setShowBank(true)}
            style={{
              padding: '12px 16px',
              fontSize: '14px',
              cursor: 'pointer',
              backgroundColor: SURFACE_MUTED,
              color: 'var(--color-text)',
              border: `1.5px solid ${BORDER}`,
              borderRadius: '999px',
              fontWeight: 500,
              minHeight: 48,
              gridColumn: incorrectAnswers.length > 0 ? undefined : '1 / -1',
            }}
          >
            View Bank ({totalAnswered})
          </button>
        </div>

        {/* More options toggle */}
        <button
          onClick={() => setShowMoreOptions(v => !v)}
          style={{
            width: '100%',
            padding: '8px',
            fontSize: '13px',
            color: TEXT_MUTED,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textAlign: 'center',
          }}
        >
          {showMoreOptions ? '▲ Fewer options' : '▼ More options'}
        </button>

        {showMoreOptions && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
            <button
              onClick={() => {
                const nextShuffle = !isShuffled;
                setIsShuffled(nextShuffle);
                setCurrentWordIndex(0);
                if (!nextShuffle) setShuffleOrder(null);
              }}
              style={{
                padding: '8px 16px',
                fontSize: '13px',
                cursor: 'pointer',
                backgroundColor: isShuffled ? PRIMARY : SURFACE_MUTED,
                color: isShuffled ? '#fff' : 'var(--color-text)',
                border: 'none',
                borderRadius: '999px',
                fontWeight: 500,
              }}
              title={isShuffled ? 'Currently shuffled' : 'Currently ordered'}
            >
              {isShuffled ? '🔀 Shuffled' : '↕ Ordered'}
            </button>
            <button
              onClick={() => setShowOnlyCustom(prev => !prev)}
              disabled={!user || customWordEntries.length === 0}
              style={{
                padding: '8px 16px',
                fontSize: '13px',
                cursor: !user || customWordEntries.length === 0 ? 'not-allowed' : 'pointer',
                backgroundColor: showOnlyCustom ? PRIMARY : SURFACE_MUTED,
                color: showOnlyCustom ? '#fff' : 'var(--color-text)',
                border: 'none',
                borderRadius: '999px',
                fontWeight: 500,
                opacity: !user || customWordEntries.length === 0 ? 0.5 : 1,
              }}
              title={!user ? 'Sign in to view your custom deck' : customWordEntries.length === 0 ? 'Add a custom word first' : ''}
            >
              {showOnlyCustom ? 'Show All Words' : `My Words (${customWordEntries.length})`}
            </button>
            <button
              onClick={() => setShowCustomWordForm(prev => !prev)}
              style={{
                padding: '8px 16px',
                fontSize: '13px',
                cursor: 'pointer',
                backgroundColor: showCustomWordForm ? PRIMARY : SURFACE_MUTED,
                color: showCustomWordForm ? '#fff' : 'var(--color-text)',
                border: 'none',
                borderRadius: '999px',
                fontWeight: 500,
              }}
            >
              {showCustomWordForm ? 'Hide Form' : '+ Add Word'}
            </button>
          </div>
        )}
      </div>

      {showCustomWordForm && (
        <div style={{ ...cardBaseStyle, marginTop: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ margin: 0 }}>Add a Custom Word</h3>
            <button
              onClick={() => setShowCustomWordForm(false)}
              style={{
                border: 'none',
                background: 'transparent',
                fontSize: '20px',
                cursor: 'pointer',
                color: TEXT_MUTED
              }}
              aria-label="Close custom word form"
            >
              ×
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '10px', marginBottom: '12px' }}>
            <input
              type="text"
              placeholder="Word"
              value={newWord}
              onChange={e => setNewWord(e.target.value)}
              style={{ flex: 1, padding: '12px 14px', borderRadius: '12px', border: `1px solid ${BORDER}` }}
            />
            <button
              onClick={handleFetchDefinition}
              disabled={lookupLoading || !newWord.trim()}
              style={{
                padding: '12px 16px',
                borderRadius: '12px',
                border: 'none',
                backgroundColor: lookupLoading ? 'var(--color-surface-muted)' : PRIMARY,
                color: '#fff',
                cursor: lookupLoading ? 'not-allowed' : 'pointer'
              }}
            >
              {lookupLoading ? 'Looking up…' : 'Auto-fill Definition'}
            </button>
          </div>
          <textarea
            placeholder="Definition"
            value={newDefinition}
            onChange={e => setNewDefinition(e.target.value)}
            rows={3}
            style={{ width: '100%', padding: '12px', borderRadius: '12px', border: `1px solid ${BORDER}`, marginBottom: '12px' }}
          />
          <input
            type="text"
            placeholder="Synonyms (optional, separated by ; )"
            value={newSynonyms}
            onChange={e => setNewSynonyms(e.target.value)}
            style={{ width: '100%', padding: '12px 14px', borderRadius: '12px', border: `1px solid ${BORDER}`, marginBottom: '12px' }}
          />
          {customWordError && (
            <p style={{ color: 'var(--color-danger)', margin: '0 0 8px' }}>{customWordError}</p>
          )}
          {customWordSuccess && (
            <p style={{ color: 'var(--color-success)', margin: '0 0 8px' }}>{customWordSuccess}</p>
          )}
          <button
            onClick={handleAddCustomWord}
            disabled={savingCustomWord}
            style={{
              padding: '12px 18px',
              borderRadius: '999px',
              border: 'none',
              backgroundColor: savingCustomWord ? 'var(--color-surface-muted)' : PRIMARY,
              color: '#fff',
              cursor: savingCustomWord ? 'not-allowed' : 'pointer',
              marginBottom: user ? 0 : '12px'
            }}
          >
            {savingCustomWord ? 'Saving…' : user ? 'Save Custom Word' : 'Log in to Save'}
          </button>
          {!user && (
            <p style={{ color: TEXT_MUTED, fontSize: '13px', margin: 0 }}>
              You can draft a word and definition without logging in. Sign in to save it to your deck.
            </p>
          )}
        </div>
      )}

      </div>

      {/* Report Modal */}
      {showReportModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            padding: '25px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '15px', fontSize: '20px' }}>
              Report Incorrect Definition
            </h3>
            <p style={{ marginBottom: '15px', color: '#666' }}>
              Word: <strong>{currentWord?.Word}</strong>
            </p>
            <p style={{ marginBottom: '15px', color: '#666' }}>
              Current Definition: <strong>{currentWord?.Definition}</strong>
            </p>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              What's wrong? (Optional)
            </label>
            <textarea
              value={reportNote}
              onChange={(e) => setReportNote(e.target.value)}
              placeholder="Please describe what's incorrect about this definition..."
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical',
                marginBottom: '20px'
              }}
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowReportModal(false);
                  setReportNote('');
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={submitReport}
                className="btn-danger"
              >
                Submit Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlashcardGame;
